import React, { useMemo, useState } from "react";
import { RefreshCw, Search } from "lucide-react";
import { PortalTherapist } from "../api/contracts";
import {
  ActionsColumnHeader,
  ClinicianRolePill,
  PortalDataTable,
  PortalTableColumn,
  RelationshipPillList,
  StatusPill,
} from "../components/DataDisplay";
import { SingleSelectPillField } from "../components/SingleSelectPillField";
import { getClinicianRolePillToken } from "../config/palette";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import {
  resolveClinicNames,
  useAdminDirectory,
} from "../hooks/useAdminDirectory";
import { PortalLink } from "../components/PortalLink";
import { RowActionMenu } from "../components/HomeworkControls";
import { formatPortalActivityDateTime, formatPortalDateTime } from "../utils/dateTime";
import { comparePortalDate, PortalSortDirection, stablePortalSort } from "../utils/tableSort";

function EmptyValue() {
  return <span className="cp-empty-value">—</span>;
}

interface TherapistDirectoryRow extends PortalTherapist {
  clinicNames: string[];
  clinicIds: string[];
}

function normalizeFilterValue(value: string): string {
  return value.trim().toLocaleLowerCase();
}

function formatRoleLabel(value: string): string {
  return value
    .trim()
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toLocaleUpperCase());
}

function therapistColumns(
  clinicianCode: string,
  activitySort: PortalSortDirection | null,
  onSortActivity: () => void,
): readonly PortalTableColumn<TherapistDirectoryRow>[] {
  return [
    {
      key: "actions",
      header: <ActionsColumnHeader />,
      headerLabel: "Actions",
      initialWidth: 60,
      minWidth: 60,
      resizable: false,
      cellClassName: "cp-table-cell--compact",
      render: (therapist) => (
        <RowActionMenu
          clinicianCode={clinicianCode}
          editPath={`/clinician-portal/admin/therapists/${encodeURIComponent(therapist.id)}/edit`}
          activityName={therapist.therapistName || "therapist"}
          canEdit
          showDuplicate={false}
          showDelete={false}
        />
      ),
    },
    {
      key: "lastPortalActiveAt",
      header: "Last Activity",
      initialWidth: 245,
      minWidth: 215,
      cellClassName: "cp-table-cell--date-time",
      sortDirection: activitySort ?? "none",
      onSort: onSortActivity,
      render: (therapist) => therapist.lastPortalActiveAt ? (
        <time dateTime={therapist.lastPortalActiveAt} title={formatPortalDateTime(therapist.lastPortalActiveAt)}>
          {formatPortalActivityDateTime(therapist.lastPortalActiveAt)}
        </time>
      ) : <EmptyValue />,
    },
    {
      key: "therapistName",
      header: "Therapist Name",
      initialWidth: 225,
      minWidth: 162,
      render: (therapist) =>
        therapist.therapistName.trim() && therapist.clinicianCode.trim() ? (
          <a
            className="cp-therapist-code-link"
            href={`https://assistant.educationforall.ai/clinician-portal?clinicianCode=${encodeURIComponent(therapist.clinicianCode.trim())}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open portal as ${therapist.therapistName.trim()} (opens in a new tab)`}
          >
            {therapist.therapistName}
          </a>
        ) : (
          therapist.therapistName || <EmptyValue />
        ),
    },
    {
      key: "role",
      header: "Role",
      initialWidth: 110,
      minWidth: 110,
      render: (therapist) =>
        therapist.role ? (
          <ClinicianRolePill role={therapist.role} />
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: "advancedDashboardEnabled",
      header: "Dashboard",
      initialWidth: 120,
      minWidth: 105,
      resizable: false,
      render: (therapist) => (
        <StatusPill
          status={therapist.advancedDashboardEnabled ? "On" : "Off"}
        />
      ),
    },
    {
      key: "email",
      header: "Email",
      initialWidth: 300,
      minWidth: 220,
      render: (therapist) => therapist.email || <EmptyValue />,
    },
    {
      key: "clinicianCode",
      header: "Clinician Code",
      initialWidth: 145,
      minWidth: 145,
      render: (therapist) =>
        therapist.clinicianCode.trim() ? (
          <code className="cp-registry-code">{therapist.clinicianCode}</code>
        ) : (
          <EmptyValue />
        ),
    },
    {
      key: "clinicNames",
      header: "Clinic",
      initialWidth: 260,
      minWidth: 180,
      render: (therapist) => (
        <RelationshipPillList names={therapist.clinicNames} />
      ),
    },
    {
      key: "status",
      header: "Status",
      initialWidth: 140,
      minWidth: 110,
      resizable: false,
      render: (therapist) =>
        therapist.status ? (
          <StatusPill status={therapist.status} />
        ) : (
          <EmptyValue />
        ),
    },
  ];
}

export function TherapistsPage({ clinicianCode }: { clinicianCode: string }) {
  const state = useAdminDirectory(clinicianCode);
  const [search, setSearch] = useState("");
  const [clinicFilter, setClinicFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dashboardFilter, setDashboardFilter] = useState("");
  const [activitySort, setActivitySort] = useState<PortalSortDirection | null>(null);
  const directory =
    state.status === "success" || state.status === "refreshing"
      ? state.data
      : undefined;
  const data = directory?.therapists;
  const clinicData = directory?.clinics;
  const available = Boolean(
    data?.found &&
      data.authorized &&
      data.therapist?.role === "admin" &&
      data.patient === null &&
      clinicData?.found &&
      clinicData.authorized &&
      clinicData.therapist?.role === "admin" &&
      clinicData.patient === null,
  );
  const rows = useMemo<TherapistDirectoryRow[]>(
    () =>
      data && clinicData
        ? data.therapists.map((therapist) => ({
            ...therapist,
            clinicNames: resolveClinicNames(therapist, clinicData.clinics),
            clinicIds: clinicData.clinics
              .filter((clinic) => clinic.therapistIds.includes(therapist.id))
              .map((clinic) => clinic.id),
          }))
        : [],
    [clinicData, data],
  );
  const clinicOptions = useMemo(
    () =>
      [...(clinicData?.clinics ?? [])].sort((left, right) =>
        left.clinicName.localeCompare(right.clinicName, undefined, {
          sensitivity: "base",
        }),
      ),
    [clinicData?.clinics],
  );
  const roleOptions = useMemo(() => {
    const rolesByKey = new Map<string, string>();

    for (const therapist of data?.therapists ?? []) {
      const role = therapist.role.trim();
      const key = normalizeFilterValue(role);
      if (key && !rolesByKey.has(key)) rolesByKey.set(key, role);
    }

    return [
      { id: "all-roles", value: "", label: "All Roles" },
      ...[...rolesByKey.entries()]
        .sort((left, right) =>
          left[1].localeCompare(right[1], undefined, { sensitivity: "base" }),
        )
        .map(([value, label]) => ({
          id: value,
          value,
          label: formatRoleLabel(label),
        })),
    ];
  }, [data?.therapists]);
  const filteredRows = useMemo(() => {
    const query = normalizeFilterValue(search);

    return rows.filter((therapist) => {
      if (clinicFilter && !therapist.clinicIds.includes(clinicFilter)) {
        return false;
      }
      if (roleFilter && normalizeFilterValue(therapist.role) !== roleFilter) {
        return false;
      }
      if (
        dashboardFilter &&
        therapist.advancedDashboardEnabled !== (dashboardFilter === "on")
      ) {
        return false;
      }
      if (!query) return true;

      return [
        therapist.therapistName,
        therapist.email,
        therapist.clinicianCode,
        therapist.role,
        therapist.status,
        ...therapist.clinicNames,
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(query);
    });
  }, [clinicFilter, dashboardFilter, roleFilter, rows, search]);
  const hasActiveFilters = Boolean(
    search.trim() || clinicFilter || roleFilter || dashboardFilter,
  );
  const resultCount = hasActiveFilters
    ? `${filteredRows.length} of ${rows.length} ${
        rows.length === 1 ? "therapist" : "therapists"
      }`
    : `${rows.length} ${rows.length === 1 ? "therapist" : "therapists"}`;
  const columns = useMemo(
    () => therapistColumns(clinicianCode, activitySort, () => {
      setActivitySort((previous) => previous === "descending" ? "ascending" : "descending");
    }),
    [clinicianCode, activitySort],
  );
  const sortedRows = useMemo(
    () => activitySort ? stablePortalSort(filteredRows, (left, right) =>
      comparePortalDate(left.lastPortalActiveAt, right.lastPortalActiveAt, activitySort),
    ) : filteredRows,
    [filteredRows, activitySort],
  );

  return (
    <ClinicianPortalLayout
      activeGlobalKey="therapists"
      clinicianCode={clinicianCode}
      therapistRole={data?.therapist?.role}
    >
      <PortalHeader
        title="Therapists"
        clinicianName={available ? data?.therapist?.therapistName : undefined}
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[{ label: "Administration" }, { label: "Therapists" }]}
          />
        }
      />
      <div className="cp-page-content cp-page-content--wide cp-page-content--sticky-list">
        {state.status === "loading" ? (
          <LoadingState label="Loading Therapists" />
        ) : null}
        {state.status === "error" ? (
          <ErrorState error={state.error} onRetry={state.refresh} />
        ) : null}
        {data && !available ? (
          <ErrorState error={new Error("Therapists are unavailable")} />
        ) : null}
        {available && data ? (
          <section aria-label="Therapists list">
            <div className="cp-content-header">
              <div>
                <p>
                  Review clinician accounts and their current access status.
                </p>
              </div>
              <strong className="cp-record-count">{resultCount}</strong>
            </div>
            <div className="cp-list-toolbar cp-list-toolbar--therapists">
              <div className="cp-list-toolbar__filters cp-list-toolbar__filters--therapists">
                <label className="cp-search-control">
                  <Search aria-hidden="true" />
                  <span className="cp-visually-hidden">Search therapists</span>
                  <input
                    type="search"
                    value={search}
                    placeholder="Search therapists"
                    onChange={(event) => setSearch(event.currentTarget.value)}
                  />
                </label>
                <label className="cp-therapist-list-filter cp-therapist-list-filter--clinic">
                  <span className="cp-visually-hidden">Filter by Clinic</span>
                  <select
                    className="cp-form-input"
                    aria-label="Filter by Clinic"
                    value={clinicFilter}
                    onChange={(event) =>
                      setClinicFilter(event.currentTarget.value)
                    }
                  >
                    <option value="">All Clinics</option>
                    {clinicOptions.map((clinic) => (
                      <option key={clinic.id} value={clinic.id}>
                        {clinic.clinicName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="cp-therapist-list-filter cp-therapist-list-filter--role">
                  <span className="cp-visually-hidden">Filter by Role</span>
                  <SingleSelectPillField
                    id="therapist-role-filter"
                    value={roleFilter}
                    options={roleOptions}
                    onChange={setRoleFilter}
                    ariaLabel="Filter by Role"
                    placeholder="All Roles"
                    searchPlaceholder="Search Roles"
                    emptyMessage="No Roles found"
                    resolveColor={(option) =>
                      option.value
                        ? getClinicianRolePillToken(option.value)
                        : "grayLight2"
                    }
                  />
                </label>
                <label className="cp-therapist-list-filter cp-therapist-list-filter--dashboard">
                  <span className="cp-visually-hidden">
                    Filter by Dashboard
                  </span>
                  <SingleSelectPillField
                    id="therapist-dashboard-filter"
                    value={dashboardFilter}
                    options={[
                      {
                        id: "all-dashboards",
                        value: "",
                        label: "All Dashboards",
                      },
                      { id: "dashboard-on", value: "on", label: "On" },
                      { id: "dashboard-off", value: "off", label: "Off" },
                    ]}
                    onChange={setDashboardFilter}
                    ariaLabel="Filter by Dashboard"
                    placeholder="All Dashboards"
                    searchPlaceholder="Search Dashboard settings"
                    emptyMessage="No Dashboard settings found"
                    resolveColor={(option) =>
                      option.value === "on" ? "greenLight1" : "grayLight2"
                    }
                  />
                </label>
              </div>
              <div className="cp-list-toolbar__actions">
                <button
                  type="button"
                  className="cp-button cp-button--secondary"
                  onClick={state.refresh}
                  disabled={state.status === "refreshing"}
                >
                  <RefreshCw aria-hidden="true" />
                  {state.status === "refreshing" ? "Refreshing…" : "Refresh"}
                </button>
                <PortalLink
                  className="cp-button cp-button--primary"
                  path="/clinician-portal/admin/therapists/new"
                  clinicianCode={clinicianCode}
                >
                  Add Therapist
                </PortalLink>
              </div>
            </div>
            {filteredRows.length ? (
              <PortalDataTable
                caption="Therapists"
                columns={columns}
                records={sortedRows}
                getRecordKey={(therapist) => therapist.id}
              />
            ) : rows.length ? (
              <EmptyState
                title="No therapists match these filters."
                description="Try a different search, clinic, role, or Dashboard setting."
              />
            ) : (
              <EmptyState
                title="No therapists found."
                description="No therapist accounts are currently available."
              />
            )}
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
